import { inject, Injectable, signal, WritableSignal } from '@angular/core';

import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import {
    ActionsEnum,
    EntityEnum,
    FieldInterventionEnum,
    LayerEnum,
    PanelActions,
} from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { MapModalService } from '@connect-services/client/services/map-modal.service';

export type ContentGridLayouts = {
    FULL_SCREEN?: ContentGridOptions;
    ONE_THIRD_HORIZONTAL?: ContentGridOptions;
    ONE_THIRD_VERTICAL?: ContentGridOptions;
    PUZZLE_ONE_THIRD_ONE_THIRD?: ContentGridOptions;
    PUZZLE_ONE_THIRD_TWO_THIRDS?: ContentGridOptions;
    PUZZLE_TWO_THIRDS_ONE_THIRD?: ContentGridOptions;
    PUZZLE_TWO_THIRDS_TWO_THIRDS?: ContentGridOptions;
    TWO_THIRDS_HORIZONTAL?: ContentGridOptions;
    TWO_THIRDS_VERTICAL?: ContentGridOptions;
};

export type ContentGridOptions = {
    class: string;
    disabledForPanels?: PanelActions[];
    path: string;
    puzzle?: ContentGridLayouts;
};

export const HORIZONTAL_HOME_GRID_PARAMS: ContentGridLayouts = {
    FULL_SCREEN: {
        class: 'map-full-screen',
        disabledForPanels: [FieldInterventionEnum.FIELD_INTERVENTION_CREATE],
        path: '/assets/images/icons/full.svg',
    },
    ONE_THIRD_HORIZONTAL: {
        class: 'map-one-third-horizontal',
        path: '/assets/images/icons/one-third-horizontal.svg',
    },
    TWO_THIRDS_HORIZONTAL: {
        class: 'map-two-thirds-horizontal',
        path: '/assets/images/icons/two-thirds-horizontal.svg',
    },
};

export const PUZZLE_1_3_HOME_GRID_PARAMS: ContentGridLayouts = {
    ONE_THIRD_VERTICAL: {
        class: 'map-one-third-vertical',
        path: '/assets/images/icons/one-third-vertical.svg',
    },
    PUZZLE_ONE_THIRD_ONE_THIRD: {
        class: 'map-puzzle-one-third-one-third',
        path: '/assets/images/icons/puzzle.svg',
    },
    PUZZLE_ONE_THIRD_TWO_THIRDS: {
        class: 'map-puzzle-one-third-two-thirds',
        path: '/assets/images/icons/puzzle.svg',
    },
};
export const PUZZLE_2_3_HOME_GRID_PARAMS: ContentGridLayouts = {
    TWO_THIRDS_VERTICAL: {
        class: 'map-two-thirds-vertical',
        path: '/assets/images/icons/two-thirds-vertical.svg',
    },
    // eslint-disable-next-line sort-keys
    PUZZLE_TWO_THIRDS_ONE_THIRD: {
        class: 'map-puzzle-two-thirds-one-third',
        path: '/assets/images/icons/puzzle.svg',
    },
    PUZZLE_TWO_THIRDS_TWO_THIRDS: {
        class: 'map-puzzle-two-thirds-two-thirds',
        path: '/assets/images/icons/puzzle.svg',
    },
};

export const VERTICAL_HOME_GRID_PARAMS: ContentGridLayouts = {
    FULL_SCREEN: {
        class: 'map-full-screen',
        path: '/assets/images/icons/full.svg',
    },
    // eslint-disable-next-line sort-keys
    TWO_THIRDS_VERTICAL: {
        class: 'map-two-thirds-vertical',
        disabledForPanels: [EntityEnum.ENTITIES_LIST, ActionsEnum.AI],
        path: '/assets/images/icons/two-thirds-vertical.svg',
        puzzle: PUZZLE_2_3_HOME_GRID_PARAMS,
    },
    // eslint-disable-next-line sort-keys
    ONE_THIRD_VERTICAL: {
        class: 'map-one-third-vertical',
        disabledForPanels: [ActionsEnum.FIELD_INTERVENTION],
        path: '/assets/images/icons/one-third-vertical.svg',
        puzzle: PUZZLE_1_3_HOME_GRID_PARAMS,
    },
};

const FULL_SCREEN_LAYOUT_KEY = 0;
const TWO_THIRDS_LAYOUT_KEY = 1;
export const ONE_THIRD_LAYOUT_KEY = 2;

export enum LAYOUT_KEY {
    TWO_THIRDS = 1,
    ONE_THIRD = 2,
}

@Injectable({
    providedIn: 'root',
})
export class ContentLayoutService {
    public readonly activeLayoutKey: WritableSignal<LAYOUT_KEY | undefined> = signal(undefined);
    public readonly currentRoutePath = signal(RoutePathEnum.PROJECTS);
    public readonly gridParams: WritableSignal<ContentGridLayouts> = signal(VERTICAL_HOME_GRID_PARAMS);
    public readonly gridParamsByFieldInterventionType: Record<FieldInterventionEnum, ContentGridLayouts> = {
        [FieldInterventionEnum.FIELD_INTERVENTION_CREATE]: HORIZONTAL_HOME_GRID_PARAMS,
        [FieldInterventionEnum.FIELD_INTERVENTION_DASHBOARD]: VERTICAL_HOME_GRID_PARAMS,
        [FieldInterventionEnum.FIELD_INTERVENTION_ENTITY_DETAIL]: PUZZLE_1_3_HOME_GRID_PARAMS,
        [FieldInterventionEnum.FIELD_INTERVENTION_ENTITY_PLANNING]: VERTICAL_HOME_GRID_PARAMS,
        [FieldInterventionEnum.FIELD_INTERVENTION_LAYER_DETAIL]: PUZZLE_1_3_HOME_GRID_PARAMS,
    };
    public readonly initialMapSizeType: WritableSignal<ContentGridOptions | null> = signal(null);
    public readonly isDataTableView: WritableSignal<boolean> = signal(false);
    public readonly mapSizeType: WritableSignal<ContentGridOptions | undefined> = signal(
        VERTICAL_HOME_GRID_PARAMS.FULL_SCREEN,
    );

    private readonly broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.VIEW);
    private readonly gridParamsByActionType: Record<ActionsEnum, ContentGridLayouts> = {
        [ActionsEnum.AI]: VERTICAL_HOME_GRID_PARAMS,
        [ActionsEnum.FIELD_INTERVENTION]: VERTICAL_HOME_GRID_PARAMS,
        [ActionsEnum.LAYER]: HORIZONTAL_HOME_GRID_PARAMS,
        [ActionsEnum.NETWORK_PATH]: VERTICAL_HOME_GRID_PARAMS,
        [ActionsEnum.SETTINGS]: VERTICAL_HOME_GRID_PARAMS,
        [ActionsEnum.STATISTICS]: VERTICAL_HOME_GRID_PARAMS,
        [ActionsEnum.SYNOPTIC]: HORIZONTAL_HOME_GRID_PARAMS,
    };
    private readonly gridParamsByEntity: Record<EntityEnum, ContentGridLayouts> = {
        [EntityEnum.ENTITIES_LIST]: VERTICAL_HOME_GRID_PARAMS,
        [EntityEnum.ENTITIES_LIST_UPDATE]: VERTICAL_HOME_GRID_PARAMS,
        [EntityEnum.ENTITY_DETAIL]: VERTICAL_HOME_GRID_PARAMS,
        [EntityEnum.TABLE_ENTITY_DETAIL]: VERTICAL_HOME_GRID_PARAMS,
    };
    private readonly gridParamsByLayerType: Record<LayerEnum, ContentGridLayouts> = {
        [LayerEnum.DEPOSIT_LAYER_DETAIL]: PUZZLE_2_3_HOME_GRID_PARAMS,
    };
    private readonly mapModalService = inject(MapModalService);
    public changeContentSize(panelAction: PanelActions | null, layoutKey?: number): void {
        const gridParams = this.findGridParams(panelAction);

        this.gridParams.set(gridParams);
        this.setLayout(panelAction, layoutKey);
    }

    public setActiveLayoutKey(layoutClass: string): void {
        switch (layoutClass) {
            case 'map-one-third-vertical':
            case 'map-one-third-horizontal':
                this.activeLayoutKey.set(LAYOUT_KEY.ONE_THIRD);
                break;
            case 'map-two-thirds-vertical':
            case 'map-two-thirds-horizontal':
                this.activeLayoutKey.set(LAYOUT_KEY.TWO_THIRDS);
                break;
            case 'map-full-screen':
                this.activeLayoutKey.set(undefined);
                break;
        }
    }

    public toggleIsDataView(): void {
        this.broadcastChannel.postMessage(<BroadcastChannelEvent<{ isTableView: boolean }>>{
            additionalData: { isTableView: !this.isDataTableView() },
            message: BroadcastChannelMessageEnum.CHANGE_VIEW,
            source: BroadcastChannelSourceEnum.HOMEPAGE,
        });

        this.isDataTableView.set(!this.isDataTableView());
    }

    private findGridParams(panelAction: PanelActions | null): ContentGridLayouts {
        if (panelAction === null) {
            return VERTICAL_HOME_GRID_PARAMS;
        }

        if (Object.keys(this.gridParamsByActionType).includes(panelAction)) {
            AssertService.isEnum<ActionsEnum>(panelAction, ActionsEnum);

            return this.gridParamsByActionType[panelAction];
        }

        if (Object.keys(this.gridParamsByFieldInterventionType).includes(panelAction)) {
            AssertService.isEnum<FieldInterventionEnum>(panelAction, FieldInterventionEnum);

            return this.gridParamsByFieldInterventionType[panelAction];
        }

        if (Object.keys(this.gridParamsByEntity).includes(panelAction)) {
            AssertService.isEnum<EntityEnum>(panelAction, EntityEnum);

            return this.gridParamsByEntity[panelAction];
        }

        if (Object.keys(this.gridParamsByLayerType).includes(panelAction)) {
            AssertService.isEnum<LayerEnum>(panelAction, LayerEnum);

            if (panelAction === LayerEnum.DEPOSIT_LAYER_DETAIL && this.mapSizeType()?.puzzle) {
                return this.mapSizeType()!.puzzle!;
            }

            return this.gridParamsByLayerType[panelAction];
        }

        return VERTICAL_HOME_GRID_PARAMS;
    }

    private setLayout(actionEnum: PanelActions | null = null, defaultLayoutKey?: number): void {
        const layoutKey = defaultLayoutKey ?? (actionEnum ? TWO_THIRDS_LAYOUT_KEY : FULL_SCREEN_LAYOUT_KEY);
        const defaultGridLayout = Object.values(this.gridParams())[layoutKey];

        this.mapSizeType.set(defaultGridLayout);
        this.initialMapSizeType.set(defaultGridLayout);

        this.mapModalService.setModalsHidden(
            defaultGridLayout.class === PUZZLE_1_3_HOME_GRID_PARAMS.PUZZLE_ONE_THIRD_ONE_THIRD?.class,
        );
    }
}
